import {Component, ElementRef, HostListener, effect, input, output, viewChild} from '@angular/core';

/**
 * Confirms "Neue TSE" — retiring the current technical security system and minting/initialising a fresh one
 * (#613) — in a modal, mirroring `_modules/company/access/revoke-confirm-dialog.component.ts`'s shape
 * (backdrop/Escape cancel, focus starts on the safe "Abbrechen" button, `busy` disables the confirm button
 * while the call is in flight).
 *
 * Names all three real consequences up front, per the decisions in `PLAN.md` (Story #614): every cash point
 * of this Standort must already be closed (the backend refuses otherwise —
 * `E_LEGAL_TSS_REINIT_BLOCKED_CASH_POINT_OPEN`), every register re-logs in on its own right after, and the
 * new PUK is shown exactly once. Dismissible like the revoke dialog (a stray Escape/backdrop click costs
 * nothing — nothing has changed yet) rather than blocked like `InviteCodeDialogComponent`.
 */
@Component({
  selector: 'app-tss-reinitialize-confirm-dialog',
  standalone: true,
  template: `
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="tss-reinit-heading"
           aria-describedby="tss-reinit-body" (click)="$event.stopPropagation()">
        <div class="space-y-4 p-4 sm:p-6">

          <h2 id="tss-reinit-heading" class="text-xl font-bold tracking-tight text-gray-900">
            Neue technische Sicherheitseinrichtung anlegen?
          </h2>

          <div id="tss-reinit-body" class="space-y-3 text-sm text-gray-700">
            <p>Diese Aktion kann nicht rückgängig gemacht werden. Bitte beachten Sie:</p>
            <ul class="list-disc space-y-1.5 pl-5">
              <li>Alle Kassenpunkte dieses Standorts müssen geschlossen sein — andernfalls wird die
                Neuinitialisierung abgelehnt.</li>
              <li>Jede Kasse dieses Standorts meldet sich danach automatisch neu an (Neustart der Anmeldung).</li>
              <li>Der PUK der neuen technischen Sicherheitseinrichtung wird nur einmal angezeigt — notieren Sie
                ihn sofort an einer sicheren Stelle.</li>
            </ul>
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <button type="button" #cancelButton (click)="cancel()"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Abbrechen
            </button>
            <button type="button" (click)="confirm()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-700 px-5 py-2.5
                           text-sm font-semibold text-white shadow-lg transition-all duration-200
                           hover:bg-theme-primary-800 active:scale-95 disabled:cursor-not-allowed
                           disabled:opacity-30 disabled:grayscale">
              Ja, neu initialisieren
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class TssReinitializeConfirmDialogComponent {
  /** Disables the confirm button while the `ReInitialize` call is in flight. */
  readonly busy = input(false);

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  constructor() {
    effect(() => {
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  protected confirm(): void {
    if (this.busy()) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
